import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

/**
 * Lets the routed page publish its own in-page navigation targets so the Cammy
 * dock can offer them.
 *
 * The dock is mounted in Layout, outside the route, so it can't reach a page's
 * tab state or DOM. Rather than teaching the dock about individual pages, each
 * page publishes a list of `{ id, label, hint, go }` entries and the dock just
 * renders them — `go` is the page's own callback, so switching tabs and
 * scrolling stay the page's concern.
 */

const PageSectionsContext = createContext({
  sections: [],
  publish: () => {},
});

export function PageSectionsProvider({ children }) {
  const [sections, setSections] = useState([]);
  const publish = useCallback((next) => setSections(next || []), []);
  const value = useMemo(() => ({ sections, publish }), [sections, publish]);
  return (
    <PageSectionsContext.Provider value={value}>
      {children}
    </PageSectionsContext.Provider>
  );
}

/** Read the current page's sections (used by the dock). */
export const usePageSections = () => useContext(PageSectionsContext).sections;

/**
 * Publish sections for as long as the calling page is mounted.
 *
 * Pass a memoised array — this effect keys on its identity, so an array rebuilt
 * every render would loop.
 */
export function usePublishPageSections(sections) {
  const { publish } = useContext(PageSectionsContext);
  useEffect(() => {
    publish(sections);
    return () => publish([]);
  }, [sections, publish]);
}

export default PageSectionsContext;
